import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import CoffeeParticles from '@/components/effects/CoffeeParticles';
import Button from '@/components/ui/Button';

const container = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2, delayChildren: 0.3 },
  },
};

const item = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

export default function HeroSection() {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowVideo(true);
      if (videoRef.current) {
        videoRef.current.play().catch(() => {
          // Autoplay blocked by browser policy, handled by poster fallback
        });
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative h-screen w-full overflow-hidden">
      {/* Static Background Image (always visible as base) */}
      <div className="absolute inset-0 z-0">
        <img
          src="/images/header-bg.jpg"
          alt="Fondo café"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Video overlay with CSS fade-in after 3 seconds */}
      <div
        className={`absolute inset-0 z-[1] transition-opacity duration-[1500ms] ease-in-out ${showVideo ? 'opacity-100' : 'opacity-0'}`}
      >
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          src="/videos/sauces_webm.webm"
          muted
          loop
          playsInline
          autoPlay
          preload="auto"
        />
      </div>

      {/* Elegant frame overlay - starts below navbar area */}
      <div className="absolute top-20 left-4 right-4 bottom-4 md:top-24 md:left-6 md:right-6 md:bottom-6 lg:top-28 lg:left-8 lg:right-8 lg:bottom-8 z-[2] pointer-events-none border border-coffee-300/30 rounded-2xl">
        {/* Corner accents */}
        <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-coffee-400/60 rounded-tl-xl" />
        <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-coffee-400/60 rounded-tr-xl" />
        <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-coffee-400/60 rounded-bl-xl" />
        <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-coffee-400/60 rounded-br-xl" />
      </div>

      {/* Dark Overlay with vignette */}
      <div className="absolute inset-0 bg-coffee-900/50 z-[1]" />
      <div
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 0%, transparent 50%, rgba(27,18,15,0.6) 100%)',
        }}
      />

      {/* Coffee Particles */}
      <CoffeeParticles />

      {/* Content */}
      <motion.div
        className="relative z-20 h-full flex flex-col items-center justify-center text-center px-4"
        variants={container}
        initial="hidden"
        animate="visible"
      >
        <motion.span
          variants={item}
          className="uppercase tracking-[0.25em] text-sm md:text-base text-white/80 mb-4"
        >
          {t('hero.subtitle')}
        </motion.span>

        <motion.h1
          variants={item}
          className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-white leading-tight mb-2"
        >
          {t('hero.title')}
        </motion.h1>

        <motion.h2
          variants={item}
          className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-coffee-400 leading-tight mb-8"
        >
          {t('hero.title2')}
        </motion.h2>

        <motion.div variants={item}>
          <Button
            variant="primary"
            size="lg"
            rightIcon={<ChevronDown className="w-5 h-5" />}
            onClick={() => {
              const el = document.querySelector('#coffeetour');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            {t('hero.cta')}
          </Button>
        </motion.div>
      </motion.div>

      {/* Floating Banner Image */}
      <div className="hidden md:block absolute right-8 lg:right-20 bottom-16 z-20">
        <motion.img
          src="/images/banner.png"
          alt="Café Los Sauces"
          className="w-40 lg:w-52 drop-shadow-2xl"
          animate={{ y: [0, -12, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20">
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown className="w-6 h-6 text-white/70" />
        </motion.div>
      </div>
    </section>
  );
}
